import { compact } from 'lodash'
import { decodeFilters } from 'app/filter.utils'

/**
 * Search string to carry across Action Center navigations: our own params only.
 *
 * Deliberately drops `wId` — WorkspaceGate owns it and stamps it via
 * `history.replaceState`, which React Router never observes. Re-writing a
 * stale `wId` from React Router's snapshot makes the gate think the workspace
 * changed, triggering switchWorkspace() + window.location.reload().
 */
export function queueNavSearch(search: string): string {
  const incoming = new URLSearchParams(search)
  const kept = new URLSearchParams()
  const filters = incoming.get(`filters`)
  if (filters) kept.set(`filters`, filters)
  const join = incoming.get(`join`)
  if (join) kept.set(`join`, join)
  const next = kept.toString()
  return next ? `?${next}` : ``
}

/**
 * The Action Center's location drill-down uses the standalone LocationFilter
 * (same square button as the contacts table), which encodes `locationId in
 * (...)` into the `filters` URL param. This reads the selected ids back out.
 */
export function readLocationIds(filtersParam: string | null): string[] {
  const filter = decodeFilters(filtersParam).find((f) => f.attribute === `locationId`)
  if (!filter || filter.operator !== `in`) return []
  const values = Array.isArray(filter.value) ? filter.value : [filter.value]
  return compact(values as string[])
}

/**
 * Appends `queueNavSearch`'s carried params onto a destination that may
 * already own a query string of its own, e.g. the Pipelines board's
 * `?tab=`/`&band=`. Plain string concatenation would produce a second `?`
 * once a board queue's destination stopped being a bare path, so this joins
 * with `&` when one is already present instead of assuming the callers of
 * `queueHref`/`QueueViewToggle` know which destinations carry their own query
 * string and which do not.
 */
export function appendQueueNavSearch(path: string, search: string): string {
  const carried = queueNavSearch(search)
  if (!carried) return path
  return path.includes(`?`) ? `${path}${carried.replace(`?`, `&`)}` : `${path}${carried}`
}
